import styled from "@emotion/styled"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import WatchCard from "components/watch-grid/watch-card/WatchCard"
import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import getMediaQuery, { breakpoint } from "css/breakpoints"
import { fullGrid } from "css/grid"
import { headline30, legend100 } from "css/text"
import useSSR from "hooks/useSSR"
import { useEffect, useRef, useState } from "react"
import { fetchWatches } from "utils/rolex.api"

import { buildContext } from "@coveo/headless"
import { buildFrequentlyViewedTogetherList, loadClickAnalyticsActions } from "@coveo/headless/product-recommendation"
import { getMultiPriceData } from "components/price/utils"

const Root = styled.section`
  position: relative;
  ${fullGrid}
  background: rgb(var(--pure-white));
`

const Grid = styled.div`
  display: contents;
`

const Heading = styled.div`
  margin-block-start: ${props => (props.version === "modelpage" ? "10vh" : "2.5rem")};
  margin-block-end: 0.625rem;
  grid-column: main;
  display: block;

  /* ${getMediaQuery("m")} {
    margin-block-start: ${props => (props.version === "modelpage" ? "10vh" : "3.75rem")};
    grid-column: col 1 / col -2;
  } */
  ${getMediaQuery("m")} {
    margin-block-start: ${props => (props.version === "modelpage" ? "10vh" : "3.75rem")};
    grid-column: col 1 / span 6;
  }

  ${getMediaQuery("l")} {
    grid-column: col 1 / span 4;
  }

  > h2 {
    ${headline30}
    display: inline;
  }
  > p {
    margin-block-start: 0.625rem;
  }
`

const Paragraph = styled.p`
  ${legend100}
  grid-column: main;

  ${getMediaQuery("m")} {
    grid-column: col 1 / span 6;
  }

  ${getMediaQuery("l")} {
    grid-column: col 1 / span 4;
  }
`

const List = styled.ul`
  grid-column: main;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 8px;
  margin-block: 50px;
  grid-auto-rows: auto;

  > div {
    background: rgb(var(--light-grey));
  }

  > li:nth-of-type(3) {
    display: none;
  }

  ${getMediaQuery("m")} {
    margin-block: 50px;
    grid-gap: var(--grid-gap);
    grid-template-columns: repeat(2, 1fr);
  }

  ${getMediaQuery("l")} {
    grid-template-columns: repeat(3, 1fr);

    > li:nth-of-type(3) {
      display: grid;
    }
  }
`

// const Refresh = styled.button`
//   position: absolute;
//   top: 3.75rem;
//   right: var(--outer-margin);
// `

const YMAL = props => {
  const console = useConsole()
  const ssr = useSSR()
  const locale = useLocale()
  const env = useEnv()
  const { countryCode } = useUser()

  const lang = locale?.current?.codes?.www

  const { heading, paragraph, rmcs: watches, personalized: isPersonalized, fallbackObject, wishlistRmcs } = props

  const [ymalData, setYmalData] = useState([])
  const [ymalLength, setYmalLength] = useState(3)

  let controller = null
  const [recommendationList, setRecommendationList] = useState(null)
  const [state, setState] = useState(undefined)

  const [personalizedData, setPersonalizedData] = useState([])
  const [fallbackData, setFallbackData] = useState([])

  const root = useRef()

  const onResize = () => {
    setYmalLength(window.innerWidth < parseInt(breakpoint.l) * 16 ? 2 : 3)
  }

  const handleRecommendationClick = recommendation => {
    window.__tcfapi("getNonIABVendorConsents", 2, nonIabConsent => {
      const vendors = nonIabConsent?.nonIabVendorConsents
      if (!nonIabConsent?.gdprApplies || vendors?.["16"] === true) {
        const { logProductRecommendationOpen } = loadClickAnalyticsActions(global.coveoRecommendationEngine)
        global.coveoRecommendationEngine.dispatch(logProductRecommendationOpen({ ...recommendation, ec_name: recommendation.additionalFields.rlx_title }))
      }
    })
  }

  // const refresh = () => {
  //   const randomWatches = selectRandom(ymalData, 3)
  //   setYmalData(randomWatches)
  // }

  const newFetchPersonalized = async ({ rmcs: coveoRmcs }, { env, locale }) => {
    // Get recommendations for a specific item
    let item = null
    if (coveoRmcs?.length > 1) {
      item = coveoRmcs
    } else {
      item = coveoRmcs ? [coveoRmcs[0]] : ""
    }

    let visitorId = null
    if (document?.cookie) {
      const splittedCookie = document.cookie.split("; ")

      const visitorIdElement = splittedCookie.find(row => row.startsWith("coveo_visitorId="))

      if (visitorIdElement) visitorId = visitorIdElement.split("=")[1]
    }

    if (process.browser && global.coveoRecommendationEngine && !controller) {
      const ctx = buildContext(global.coveoRecommendationEngine)
      ctx.set({ lang, contenttype: "watch" })

      try {
        window.__tcfapi("getNonIABVendorConsents", 2, nonIabConsent => {
          const vendors = nonIabConsent?.nonIabVendorConsents
          if (!nonIabConsent?.gdprApplies || vendors?.["16"] === true) {
            global.coveoRecommendationEngine.enableAnalytics()
          } else {
            global.coveoRecommendationEngine.disableAnalytics()
          }
        })
      } catch {
        global.coveoRecommendationEngine.enableAnalytics()
      }

      const fieldsToInclude = [
        "rlx_contenttype",
        "rlx_rmc",
        "rlx_title",
        "rlx_family",
        "rlx_family_facet",
        "rlx_case_title",
        "rlx_newmodelselection",
        "rlx_familycode",
        "rlx_titlecode",
        "rlx_description",
        "rlx_searchimage",
        "rlx_search_section",
        "rlx_search_title",
        "rlx_path",
        "source",
        "sourceName",
      ]

      controller = buildFrequentlyViewedTogetherList(global.coveoRecommendationEngine, {
        options: {
          maxNumberOfRecommendations: 3,
          additionalFields: fieldsToInclude,
        },
      })
      setState(controller.state)
      controller.subscribe(() => {
        setState(controller?.state)
        setRecommendationList(controller?.state?.recommendations)
      })

      await controller.setSkus(item)
      // setTimeout(() => {
      await controller.refresh()
      // }, 1000)
    }
    // loadRecommendationActions({id: })
  }

  const observer = useRef(null)

  useEffect(() => {
    const results = recommendationList?.map(result => {
      const { additionalFields: raw } = result
      if (!raw) return null

      const newResult = {
        rmc: raw.rlx_rmc,
        title: raw.rlx_title,
        family: raw.rlx_family[0],
        familyCode: raw.rlx_familycode,
        titleCode: raw.rlx_familycode,
        facet_case_title: raw.rlx_case_title[0],
        recommendation: result,
        newmodelselection: raw.rlx_newmodelselection,
      }

      return newResult
    })

    setPersonalizedData(results || [])
  }, [recommendationList])

  const init = () => {
    const splittedPath = window.location.pathname.split("/watches/")

    const urlModel = splittedPath?.[1]?.split("/")[1]

    console.log({ urlModel })

    // get third element of pathname
    const currentModel = urlModel ? [urlModel] : []

    let watchesRmc = []

    if (isPersonalized) {
      // if route is /wishlist
      if (window.location.pathname.includes("/wishlist") || !currentModel) {
        // if wishlist has no watches
        if (!wishlistRmcs || wishlistRmcs.length === 0) {
          watchesRmc = watches.map(({ rmc }) => rmc)
        } else {
          watchesRmc = wishlistRmcs.map(({ rmc }) => rmc)
        }
      }
      // if route is /model
      else {
        watchesRmc = currentModel
      }
    } else {
      // if route is /wishlist
      if (window.location.pathname === "/wishlist" || !currentModel) {
        watchesRmc = watches.map(watch => watch?.rmc)
      }
      // if route is /model
      else {
        watchesRmc = fallbackObject
      }
    }

    const fetchYmalData = async () => {
      if (isPersonalized) {
        await newFetchPersonalized({ rmcs: watchesRmc }, { env, locale })
      }
      const fallbackResults = await fetchWatches({ rmcs: fallbackObject || watches.map(watch => watch?.rmc), lang }, { env, locale })
      setFallbackData(fallbackResults || [])
    }
    fetchYmalData()
  }

  useEffect(() => {
    const newYmalData = [...personalizedData, ...fallbackData].slice(0, 3)
    setYmalData(newYmalData)
  }, [fallbackData, personalizedData])

  useEffect(() => {
    init()
  }, [wishlistRmcs])

  useEffect(() => {
    if (!ssr) window.addEventListener("resize", onResize)

    return () => {
      if (!ssr) window.removeEventListener("resize", onResize)
    }
  }, [ssr])

  async function trigger() {
    const dataproducts = ymalData
      .filter(Boolean)
      .map(({ rmc, familyCode, titleCode }) => ({ productCategory: familyCode, titleCode, productID: rmc, productQuantity: "1" }))
    const rmcs = dataproducts.reduce((acc, { productID }) => acc.concat([productID]), [])
    const prices = await getMultiPriceData({ rmcs, countryCode, locale, env })
    const currency = prices.popin.currency

    const products = dataproducts.map(prod => {
      const priceData = prices.priceList.find(p => p.rmc === prod.productID)
      return {
        productCategory: prod.productCategory,
        productModel: prod.titleCode,
        productID: prod.productID,
        productQuantity: "1",
        productPrice: priceData.price,
        currency: currency,
      }
    })

    waTriggerEvent({
      eventName: "ymalDisplayed",
      eventType: "Impression",
      products,
    })
  }

  useEffect(() => {
    // intersection observer
    if (ymalData?.filter(Boolean).length === 0) return
    const ymalElement = root.current
    const observer = new IntersectionObserver(([{ isIntersecting }]) => {
      if (isIntersecting) {
        trigger()
        observer.disconnect()
      }
    })

    const tm = setTimeout(() => {
      observer.observe(ymalElement)
    }, 3000)

    return () => {
      clearTimeout(tm)
      // Disconnect the observer after the event is triggered
      observer.disconnect()
    }
  }, [ymalData])

  // only show 2 watches on mobile
  const ymalDataByWidth = ymalData?.length > 2 ? ymalData.slice(0, ymalLength) : ymalData

  return (
    <Root ref={root}>
      <Heading {...props}>
        <h2>{heading}</h2>
        <Paragraph>{paragraph}</Paragraph>
      </Heading>

      {ymalData?.length > 0 && (
        <Grid>
          <List>
            {ymalDataByWidth.filter(Boolean).map((watch, index) => (
              <div
                key={index}
                onClick={() => handleRecommendationClick(watch.recommendation)}
                //onContextMenu={() => handleRecommendationClick(watch.recommendation)}
              >
                <WatchCard
                  key={watch.rmc}
                  rmc={watch.rmc}
                  title={watch.title}
                  family={watch.family}
                  familyCode={watch.familyCode}
                  facet_case_title={watch.facet_case_title}
                  newmodelselection={watch.newmodelselection}
                  source='ymal'
                  sourceName={watch.recommendation?.additionalFields.sourceName || watch.recommendation?.additionalFields.source}
                />
              </div>
            ))}
          </List>
        </Grid>
      )}
      {/* Feature disabled for now */}
      {/* <Refresh onClick={refresh}>Refresh</Refresh> */}
    </Root>
  )
}

export default YMAL
